import { useSelector } from "react-redux";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import AdminShell from "./AdminShell";

const API = process.env.REACT_APP_API_URL || "";

const C = {
  green: "#0e3725",
  gold: "#c9a84c",
  red: "#dc2626",
  border: "#e5e7eb",
  muted: "#6b7280",
};

const QuarterlyReport = () => {
  const { propertyId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quarter, setQuarter] = useState("Q2");
  const [year, setYear] = useState(new Date().getFullYear());
  const printRef = useRef();

  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const authHeader = { Authorization: `Bearer ${token}` };

  const loadReport = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/reports/quarterly/${propertyId}?quarter=${quarter}&year=${year}`, {
      headers: authHeader,
    })
      .then((r) => r.json())
      .then((data) => setReport(data.report || null))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [propertyId, quarter, year, token]);

  useEffect(() => {
    loadReport();
  }, []);

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Quarterly Report — ${report?.property?.title}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111; padding: 32px; }
            h1 { color: #0e3725; font-size: 22px; }
            h2 { color: #0e3725; font-size: 16px; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px; margin-top: 28px; }
            table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 10px; }
            th { background: #f3f4f6; padding: 8px 10px; text-align: left; font-weight: 700; color: #374151; }
            td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; }
            .gold { color: #c9a84c; font-weight: 700; }
            .green { color: #166534; font-weight: 700; }
            .red { color: #dc2626; font-weight: 700; }
            .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 12px; }
            .summary-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px 16px; }
            .label { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
            .value { font-size: 20px; font-weight: 800; }
            .footer { margin-top: 40px; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 12px; }
            @media print { body { padding: 16px; } }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  const pnlColor = report?.tokens?.unrealizedPnlPerToken >= 0 ? "#166534" : "#dc2626";
  const pnlSign = report?.tokens?.unrealizedPnlPerToken >= 0 ? "+" : "";

  return (
    <AdminShell pageTitle="Quarterly Report">
      {/* Controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <select
            value={quarter}
            onChange={(e) => setQuarter(e.target.value)}
            style={{ padding: "8px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
          >
            <option value="Q1">Q1 (Jan–Mar)</option>
            <option value="Q2">Q2 (Apr–Jun)</option>
            <option value="Q3">Q3 (Jul–Sep)</option>
            <option value="Q4">Q4 (Oct–Dec)</option>
          </select>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            style={{ padding: "8px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={loadReport}
            style={{
              padding: "8px 18px",
              background: C.green,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Generate
          </button>
        </div>
        {report && (
          <button
            onClick={handlePrint}
            style={{
              padding: "8px 18px",
              background: "linear-gradient(135deg,#c9a84c,#7a5c10)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Print / Save PDF
          </button>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: "center", color: C.muted, padding: "60px 0" }}>Generating report…</div>
      )}

      {report && !loading && (
        <div
          ref={printRef}
          style={{ background: "#fff", borderRadius: 12, padding: 36, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}
        >
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, borderBottom: "3px solid #0e3725", paddingBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                Treasury Real Estate Tokenization
              </div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.green }}>
                Quarterly Investor Report
              </h1>
              <div style={{ fontSize: 15, color: C.gold, fontWeight: 700, marginTop: 4 }}>
                {report.period.quarter} {report.period.year} — {report.property.title}
              </div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
                {report.property.city} · {report.property.sector}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: C.muted }}>Generated</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                {new Date(report.generatedAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}
              </div>
            </div>
          </div>

          {/* Summary KPIs */}
          <h2 style={{ fontSize: 14, fontWeight: 700, color: C.green, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 16 }}>
            Summary
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }}>
            {[
              { label: "Current Property Value", value: `SAR ${(report.summary.currentPropertyValue || 0).toLocaleString()}`, color: C.green },
              { label: "Total Expenses", value: `SAR ${(report.summary.totalExpenses || 0).toLocaleString()}`, color: C.red },
              { label: "Dividends Distributed", value: `SAR ${(report.summary.totalDividendsDistributed || 0).toLocaleString()}`, color: "#166534" },
              { label: "Net Income", value: `SAR ${(report.summary.netIncome || 0).toLocaleString()}`, color: report.summary.netIncome >= 0 ? "#166534" : C.red },
            ].map((kpi) => (
              <div key={kpi.label} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px" }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{kpi.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
              </div>
            ))}
          </div>

          {/* Token Metrics */}
          <h2 style={{ fontSize: 14, fontWeight: 700, color: C.green, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 16 }}>
            Token Metrics
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }}>
            {[
              { label: "Total Token Supply", value: (report.tokens.totalSupply || 0).toLocaleString() },
              { label: "Tokens Issued", value: (report.tokens.tokensIssued || 0).toLocaleString() },
              { label: "Token Holders", value: report.tokens.holderCount },
              { label: "Initial Token Price", value: `SAR ${(report.tokens.initialPrice || 0).toLocaleString()}` },
            ].map((m) => (
              <div key={m.label} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px" }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{m.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* Intrinsic Value & P&L */}
          <div style={{ background: C.green, borderRadius: 12, padding: "20px 24px", marginBottom: 32, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Intrinsic Token Value</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.gold }}>
                SAR {(report.tokens.intrinsicValue || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                = Property Value ÷ Total Supply
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Unrealized P&L per Token</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: report.tokens.unrealizedPnlPerToken >= 0 ? "#86efac" : "#fca5a5" }}>
                {pnlSign}SAR {Math.abs(report.tokens.unrealizedPnlPerToken || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                {pnlSign}{report.tokens.unrealizedPnlPct || 0}% vs. initial price
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Total Unrealized P&L</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: report.tokens.totalUnrealizedPnl >= 0 ? "#86efac" : "#fca5a5" }}>
                {pnlSign}SAR {Math.abs(report.tokens.totalUnrealizedPnl || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                Across all issued tokens
              </div>
            </div>
          </div>

          {/* Latest Evaluation */}
          <h2 style={{ fontSize: 14, fontWeight: 700, color: C.green, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 16 }}>
            Property Evaluation
          </h2>
          {report.evaluation.latest ? (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: "18px 20px", marginBottom: 32 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Period</div>
                  <div style={{ fontWeight: 700, color: C.gold, fontSize: 15 }}>{report.evaluation.latest.period}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Evaluator 1</div>
                  <div style={{ fontWeight: 600 }}>{report.evaluation.latest.evaluator1?.name || "—"}</div>
                  <div style={{ fontSize: 12, color: "#374151" }}>
                    {report.evaluation.latest.evaluator1?.value ? `SAR ${report.evaluation.latest.evaluator1.value.toLocaleString()}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Evaluator 2</div>
                  <div style={{ fontWeight: 600 }}>{report.evaluation.latest.evaluator2?.name || "—"}</div>
                  <div style={{ fontSize: 12, color: "#374151" }}>
                    {report.evaluation.latest.evaluator2?.value ? `SAR ${report.evaluation.latest.evaluator2.value.toLocaleString()}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Average Value</div>
                  <div style={{ fontWeight: 800, color: "#166534", fontSize: 18 }}>
                    SAR {(report.evaluation.latest.averageValue || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: C.muted, fontSize: 13, marginBottom: 32 }}>No evaluation recorded for this period.</div>
          )}

          {/* Expenses */}
          <h2 style={{ fontSize: 14, fontWeight: 700, color: C.green, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 16 }}>
            Expenses — {report.period.quarter} {report.period.year}
          </h2>
          {report.expenses.items.length > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 32 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: C.muted, fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: C.muted, fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: C.muted, fontWeight: 600 }}>Amount (SAR)</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: C.muted, fontWeight: 600 }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {report.expenses.items.map((ex) => (
                  <tr key={ex._id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "10px 12px" }}>{new Date(ex.date).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
                    <td style={{ padding: "10px 12px" }}>{ex.expenseTypeId?.name || "—"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: C.red }}>
                      {ex.amount.toLocaleString()}
                    </td>
                    <td style={{ padding: "10px 12px", color: C.muted, fontSize: 12 }}>{ex.description || "—"}</td>
                  </tr>
                ))}
                <tr style={{ background: "#f9fafb", fontWeight: 700 }}>
                  <td colSpan={2} style={{ padding: "10px 12px" }}>Total Expenses</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: C.red, fontSize: 15 }}>
                    SAR {report.expenses.total.toLocaleString()}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          ) : (
            <div style={{ color: C.muted, fontSize: 13, marginBottom: 32 }}>No expenses recorded for this period.</div>
          )}

          {/* Footer */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, marginTop: 8, fontSize: 11, color: C.muted, display: "flex", justifyContent: "space-between" }}>
            <span>Treasury Real Estate Tokenization Platform · treasury.sa</span>
            <span>Generated {new Date(report.generatedAt).toLocaleString("en-SA")}</span>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default QuarterlyReport;
